import { createContext, useContext, useEffect, useMemo, useReducer, useRef } from "react"
import { FilterIds, FilterQueryArgs, useFilterQueryParams } from "./filter_query_params"
import { useAppContext } from "@ciq/shared/hooks/context"
import { formatNumber, formatPercentage, formatString } from "@ciq/shared/utils/format"

import WorkerManager from "@ciq/shared/workers/WorkerManager"
import { ErrorToast } from "@ciq/shared/utils/toast"
import {
  deleteDatasourceData,
  getCampusFlowData,
  getDatasourceData,
  getHourlyData,
  getResourceData,
  getSemesterData,
  setDatasourceData,
} from "../workers/store"
import Api from "@ciq/shared/api/api"
import { parseDatasourceResponse } from "../workers/data"
import { mean } from "@ciq/shared/utils/math"
import { AttributeSort } from "@ciq/shared/utils/sort"
import { isEmptyObject } from "@ciq/shared/utils/object"
import { isEmptyArray } from "@ciq/shared/utils/array"
import { useSearchQueryParams } from "../../../hooks/search_query_params"
import {
  buildColorRanges,
  DEFAULT_METRIC_FILL_COLOR,
  END_ZONE_BUILDING_FILL_COLOR,
  getColor,
} from "../utils/colors"
import { EMPTY_ZONE_DATA_TAG, generateZonePopupContent } from "../utils/zone_content"
import { generateFlowPopupContent } from "../utils/campus_flow_content"
import eventBus from "@ciq/shared/hooks/eventBus"
import { ZoneType } from "@ciq/shared/utils/const"
import { convertHourToTime, Days } from "../../../utils/date/manager"
import { createFloorFanPattern } from "../utils/fanPattern"

const Y_GRAPH_PERCENTAGE_LABEL_STEP = 10 // has to be a mod of 5
const Y_GRAPH_INTEGER_LABEL_STEP = 0.1
const Y_GRAPH_LABEL_COUNT = 6 // Number of y-axis labels to display

/**
 * @typedef {Object} ViewData - View-related data and functions
 * @property {boolean} hasResource - Boolean indicating if a resource is currently selected
 * @property {boolean} isHidingLeftPane - Boolean indicating if the left pane is hidden
 * @property {() => void} toggleLeftPane - Toggles the visibility of the left pane
 * @property {boolean} isHidingRightPane - Boolean indicating if the right pane is hidden
 * @property {() => void} toggleRightPane - Toggles the visibility of the right pane
 */

/**
 * @typedef {Object} MapViewDataResult
 * @property {ViewData} view - View-related data and functions for pane management
 */

// Create context for sharing map view data across components
const MapViewDataContext = createContext()

/**
 * Provider component that wraps the map view and provides shared data access.
 * Automatically loads school resources and datasource mappings on mount.
 * Manages resource selection state, pane visibility state and provides toggle functions for UI control.
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to render
 * @returns {JSX.Element} Provider component wrapping children
 */
export function MapViewDataProvider({ children }) {
  const data = MapViewData()
  return <MapViewDataContext.Provider value={data}>{children}</MapViewDataContext.Provider>
}

export const SET_DATA = "SET_DATA"
export const SET_RESOURCE_ID = "SET_RESOURCE_ID"
export const SET_ZONE_IDS = "SET_ZONE_IDS"
export const SET_FILTER_ZONE_VIEW_TYPE = "SET_FILTER_ZONE_VIEW_TYPE"
export const SET_FILTER_MAP_DISPLAY_TYPE = "SET_FILTER_MAP_DISPLAY_TYPE"
export const SET_FILTER_DAYS = "SET_FILTER_DAYS"
export const SET_FILTER_HOUR = "SET_FILTER_HOUR"
export const SET_FILTER_ZONE_SELECTION = "SET_FILTER_ZONE_SELECTION"
export const SET_SEMESTER_METRIC = "SET_SEMESTER_METRIC"
export const SET_HOURLY_METRIC = "SET_HOURLY_METRIC"
export const SET_IS_FILTERING = "SET_IS_FILTERING"

/**
 * Builds semester pane data by grouping columns and their attribute values.
 * Filters out null/undefined attribute values (hides them from UI) while preserving zero values.
 *
 * @param {Array<string>} groups - Array of group names to organize columns
 * @param {Array<Object>} columns - Column definitions with group, name, label, formatValue
 * @param {Object} attributes - Zone attribute values keyed by column name
 * @returns {Array<Object>|null} Array of group objects with title and attributes, or null if:
 *   - groups is empty/null
 *   - columns is empty/null
 *   - attributes is empty/null
 *   - all groups end up empty after filtering null/undefined values
 */
export const buildSemesterPaneData = (groups, columns, attributes) => {
  if (isEmptyArray(groups) || isEmptyArray(columns) || isEmptyObject(attributes)) {
    return null
  }

  const out = []
  const columnGroupLookup = {}
  for (const column of columns) {
    columnGroupLookup[column.group] ??= []
    columnGroupLookup[column.group].push(column)
  }

  // Iterate through each group in the datasource table
  groups.forEach(groupName => {
    const groupAttributes = []

    // Skip groups with no matching columns
    if (!columnGroupLookup[groupName]) {
      return
    }

    // Process each column in the group
    columnGroupLookup[groupName].forEach(column => {
      const rawValue = attributes?.[column.name]

      // Skip null/undefined values - hide them entirely from the UI
      // Uses loose equality to catch both null and undefined
      if (rawValue == null) {
        return
      }

      groupAttributes.push({
        id: column.id,
        label: column.label,
        description: column.description || column.label,
        value: formatString(column.formatValue(rawValue)),
      })
    })

    // Only include groups that have at least one attribute with data
    if (groupAttributes.length > 0) {
      out.push({
        id: groupName,
        title: groupName,
        attributes: groupAttributes.sort(AttributeSort("label")),
      })
    }
  })

  return out.length > 0 ? out : null
}

export const buildMapData = (zone, columns, colorRanges, attributes) => {
  if (
    isEmptyObject(zone) ||
    isEmptyArray(columns) ||
    isEmptyObject(colorRanges) ||
    isEmptyObject(attributes)
  ) {
    return {
      colors: {},
      popupData: null,
    }
  }

  const colors = {}
  for (const column of columns) {
    colors[column.name] = getColor(colorRanges[column.name], attributes[column.name])
  }

  const popupData = generateZonePopupContent(zone, columns, attributes)

  return {
    colors,
    popupData,
  }
}

export const buildHourlyGraphMetricBody = (column, value, colorRanges, maxValue, popupData) => {
  if (column == null || value == null || colorRanges == null || maxValue == null) {
    return {
      label: "--",
      value,
      percentage: null,
      color: DEFAULT_METRIC_FILL_COLOR,
      popupData: null,
    }
  }

  if (!maxValue) {
    maxValue = 1
  }

  return {
    label: column.formatValue(value),
    value: column.normalizeValue(value),
    percentage: formatPercentage(value / maxValue, { isNormalized: true }),
    color: getColor(colorRanges, value),
    popupData,
  }
}

export const buildHourlyLegend = (hourlyColumn, hourlyColorRanges) => {
  return {
    show: !isEmptyObject(hourlyColumn) && !isEmptyArray(hourlyColorRanges),
    count: hourlyColumn != null && hourlyColorRanges?.length > 0 ? hourlyColorRanges.length : 0,
    anchor: {
      start: hourlyColumn?.formatValue(hourlyColorRanges?.[0]?.min),
      end: hourlyColumn?.formatValue(hourlyColorRanges?.[hourlyColorRanges.length - 1]?.max),
    },
    get: index => {
      const range = hourlyColorRanges?.[index]
      return {
        title: range?.title,
        label: range?.range,
        color: range?.color,
      }
    },
  }
}

const EMPTY_ARRAY = []
const EMPTY_SET = new Set()
const EMPTY_GEOJSON = { type: "FeatureCollection", features: [] }
const EMPTY_SEMESTER_PANE_DATA = buildSemesterPaneData()
const EMPTY_SEMESTER_MAP_DATA = buildMapData()
const EMPTY_HOURLY_PANE_DATA = buildHourlyGraphMetricBody()
const EMPTY_HOURLY_MAP_DATA = buildMapData()

export const DEFAULT_DATA = {
  raw: null,
  resource: {
    id: null,
    zone: null,
  },
  zoneIds: null,
  fanGeojson: null,
  isFiltering: false,
  filters: {
    days: null,
    hour: null,
    mapDisplayType: null,
    zoneViewType: null,
    usages: null,
    buildings: null,
    floors: null,
    loweredSearch: null,
  },
  metrics: {
    semester: null,
    hourly: null,
  },
  columns: {
    semester: null,
    hourly: null,
  },
  maxValues: {
    semester: null,
    hourly: null,
  },
  colorRanges: {
    semester: null,
    hourly: null,
  },
  content: {
    semester: {
      zoneData: {},
      mapData: {},
    },
    hourly: {
      zoneData: {},
      mapData: {},
    },
  },
  data: {
    resources: {
      hasFetched: null,
      isFiltering: false,
      hierarchy: null,
      zone: null,
      zones: EMPTY_ARRAY,
      zoneIds: EMPTY_SET,
      usages: EMPTY_ARRAY,
      // eslint-disable-next-line no-unused-vars
      getGeoJsonFeature: zoneId => null,
      // eslint-disable-next-line no-unused-vars
      getCampusBounds: zoneId => null,
      // eslint-disable-next-line no-unused-vars
      getParent: zone => null,
      // eslint-disable-next-line no-unused-vars
      getZoneParentIds: zoneId => null,
      // eslint-disable-next-line no-unused-vars
      subZoneIter: zone => [],
      getFloorZones: () => EMPTY_ARRAY,
      // eslint-disable-next-line no-unused-vars
      getUsage: usageId => null,
      buildings: EMPTY_ARRAY,
      // eslint-disable-next-line no-unused-vars
      getBuilding: buildingId => null,
      floors: EMPTY_ARRAY,
      types: EMPTY_ARRAY,
      initialBounds: null,
    },
    semesterSummary: {
      metric: null,
      table: null,
      column: null,
      zoneData: null,
      mapData: null,
      maxValues: null,
    },
    map: {
      baseGeoJson: EMPTY_GEOJSON,
      // eslint-disable-next-line no-unused-vars
      getBuildingFeature: buildingId => null,
      // eslint-disable-next-line no-unused-vars
      getBuildingFloorFeatures: buildingId => EMPTY_ARRAY,
      // eslint-disable-next-line no-unused-vars
      getDefaultFeatures: (buildingId, floor) => EMPTY_ARRAY,
    },
  },
}

export const updateResourceInfo = state => {
  const zoneId = state.resource.id

  return {
    ...state,
    resource: {
      ...state.resource,
      zone: state.raw?.resourceData?.getZoneById(zoneId),
    },
  }
}

export const getColorRanges = (column, maxValues, apiColorRanges) => {
  if (isEmptyObject(column) || isEmptyObject(maxValues)) {
    return null
  }

  if (!apiColorRanges) {
    throw new Error("API contract violation: colorRanges is required")
  }

  let colorRanges = null

  if (maxValues != null) {
    colorRanges = {}
    for (const [metric, maxValue] of Object.entries(maxValues)) {
      colorRanges[metric] = buildColorRanges(column, maxValue, apiColorRanges)
    }
  }

  return colorRanges
}

export const updateSemsterDatasetInfo = state => {
  const metric = state.metrics.semester
  const column = state.raw?.datasourceData?.semesterTable?.columns?.find(c => c.name === metric)
  const maxValues = state.raw?.semesterData?.maxValues
  const apiColorRanges = state.raw?.datasourceData?.colorRanges

  return {
    ...state,
    columns: {
      ...state.columns,
      semester: column,
    },
    maxValues: {
      ...state.maxValues,
      semester: maxValues,
    },
    colorRanges: {
      ...state.colorRanges,
      semester: getColorRanges(column, maxValues, apiColorRanges),
    },
  }
}

export const updateHourlyDatasetInfo = state => {
  const metric = state.metrics.hourly
  const column = state.raw?.datasourceData?.hourlyTable?.columns?.find(c => c.name === metric)
  const maxValues = state.raw?.hourlyData?.maxValues
  const apiColorRanges = state.raw?.datasourceData?.colorRanges

  return {
    ...state,
    columns: {
      ...state.columns,
      hourly: column,
    },
    maxValues: {
      ...state.maxValues,
      hourly: maxValues,
    },
    colorRanges: {
      ...state.colorRanges,
      hourly: getColorRanges(column, maxValues, apiColorRanges),
    },
  }
}

export const updateSemesterContentInfo = state => {
  const resourceData = state.raw?.resourceData
  const datasourceData = state.raw?.datasourceData
  const semesterData = state.raw?.semesterData

  if (isEmptyObject(resourceData) || isEmptyObject(datasourceData) || isEmptyObject(semesterData)) {
    return state
  }

  const enabledColumns = datasourceData.semesterTable.enabledColumns
  const mapEnabledColumns = datasourceData.semesterTable.mapEnabledColumns

  const zoneData = {}
  const mapData = {}

  for (const zone of resourceData.zones) {
    const metrics = semesterData?.zoneData?.[zone.id]
    if (metrics == null) {
      zoneData[zone.id] = EMPTY_SEMESTER_PANE_DATA
      mapData[zone.id] = EMPTY_SEMESTER_MAP_DATA
    } else {
      zoneData[zone.id] = buildSemesterPaneData(datasourceData?.groups, enabledColumns, metrics)
      mapData[zone.id] = buildMapData(zone, enabledColumns, state.colorRanges.semester, metrics)
    }
  }

  return {
    ...state,
    content: {
      ...state.content,
      semester: {
        zoneData,
        mapData,
      },
    },
  }
}

/**
 * Computes hourly content data in a single pass through all zones.
 * Optionally computes byDay formatted data and always computes byHour aggregates and map data.
 *
 * @param {Object} state - The reducer state
 * @returns {Object} Updated state with computed hourly content
 */
export const updateHourlyContentInfo = state => {
  const resourceData = state.raw?.resourceData
  const datasourceData = state.raw?.datasourceData
  const hourlyData = state.raw?.hourlyData
  const metric = state.metrics.hourly
  const hour = state.filters.hour
  const selectedDays = state.filters.days
  const colorRanges = state.colorRanges.hourly?.[metric]
  const column = state.columns.hourly

  if (
    isEmptyArray(resourceData?.zones) ||
    isEmptyArray(datasourceData?.hourlyTable.enabledColumns) ||
    isEmptyObject(hourlyData) ||
    isEmptyArray(colorRanges) ||
    isEmptyArray(selectedDays) ||
    isEmptyObject(column)
  ) {
    return state
  }

  const mapData = {
    colors: {},
    popups: {},
  }

  const getValue = zoneData => {
    const out = {}
    if (isEmptyObject(zoneData)) {
      return out
    }

    for (const column of datasourceData.hourlyTable.enabledColumns) {
      const values = zoneData[column.name]?.[hour]
      if (isEmptyObject(values)) {
        continue
      }

      // Filter by selected days and compute mean
      const data = Object.entries(values)
        .filter(([day]) => selectedDays.includes(parseInt(day)))
        .map(([, value]) => value)

      if (!data.length) {
        continue
      }

      out[column.name] = mean(data)
    }

    return out
  }

  for (const zone of resourceData.zones) {
    const values = getValue(hourlyData?.byHourData?.[zone.id])
    if (isEmptyObject(values)) {
      mapData.colors[zone.id] = DEFAULT_METRIC_FILL_COLOR
      continue
    }

    const aggregatedValue = values[metric]
    mapData.colors[zone.id] =
      zone.type === ZoneType.BUILDING && state.filters.zoneViewType === ZoneType.DEFAULT
        ? END_ZONE_BUILDING_FILL_COLOR
        : getColor(colorRanges, aggregatedValue)

    // Store popup data for this zone
    mapData.popups[zone.id] = generateZonePopupContent(
      zone,
      datasourceData.hourlyTable.mapEnabledColumns,
      values
    )
  }

  return {
    ...state,
    content: {
      ...state.content,
      hourly: {
        ...state.content.hourly,
        mapData,
      },
    },
  }
}

/**
 * Updates zone filters by applying AND logic (intersection) across all active filters.
 * Zones must match ALL applied filters to be included in the result.
 * Parent zone IDs are automatically included after filtering.
 *
 * @param {Object} state - The reducer state containing filters and resource data
 * @returns {Object} Updated state with filtered zoneIds
 */
export const updateZoneFilters = state => {
  const resourceData = state.raw?.resourceData
  const { usages, buildings, floors, loweredSearch } = state.filters
  const resourceId = state.resource.id

  if (isEmptyObject(resourceData)) {
    return state
  }

  // If no filters, clear zoneIds
  if (
    isEmptyArray(usages) &&
    isEmptyArray(buildings) &&
    isEmptyArray(floors) &&
    loweredSearch == null
  ) {
    return {
      ...state,
      zoneIds: null,
      isFiltering: false,
    }
  }

  // Start with all zones and apply filters using AND logic (intersection)
  let zoneIds = new Set(resourceData.zones.map(zone => zone.id))

  // If resourceId is set, start with just that zone and its children
  if (resourceId) {
    const resourceZoneIds = new Set([resourceId])
    // Add all child zones of the resource
    const resourceZone = resourceData.getZoneById(resourceId)
    if (resourceZone) {
      for (const zone of resourceData.zones) {
        const parentIds = resourceData.getZoneParentIds(zone.id)
        if (parentIds.has(resourceId)) {
          resourceZoneIds.add(zone.id)
        }
      }
    }
    zoneIds = resourceZoneIds
  }

  // Apply usage filter (intersection)
  if (!isEmptyArray(usages)) {
    const usageZoneIds = new Set()
    for (const usageId of usages) {
      for (const zoneId of resourceData.getZoneIdsByUsageId(usageId)) {
        usageZoneIds.add(zoneId)
      }
    }
    zoneIds = new Set([...zoneIds].filter(id => usageZoneIds.has(id)))
  }

  // Apply buildings filter (intersection)
  if (!isEmptyArray(buildings)) {
    const buildingZoneIds = new Set()
    for (const buildingId of buildings) {
      for (const zoneId of resourceData.getZoneIdsByBuildingId(buildingId)) {
        buildingZoneIds.add(zoneId)
      }
    }
    zoneIds = new Set([...zoneIds].filter(id => buildingZoneIds.has(id)))
  }

  // Apply floors filter (intersection)
  if (!isEmptyArray(floors)) {
    const floorZoneIds = new Set()
    for (const floor of floors) {
      for (const zoneId of resourceData.getZoneIdsByFloor(floor)) {
        floorZoneIds.add(zoneId)
      }
    }
    zoneIds = new Set([...zoneIds].filter(id => floorZoneIds.has(id)))
  }

  // Apply search filter (intersection)
  if (loweredSearch != null) {
    const searchZoneIds = new Set()
    for (const zone of resourceData.zones) {
      if (zone.loweredName.includes(loweredSearch)) {
        searchZoneIds.add(zone.id)
      }
    }
    zoneIds = new Set([...zoneIds].filter(id => searchZoneIds.has(id)))
  }

  // Expand parent zone IDs
  for (const zoneId of zoneIds) {
    for (const parentId of resourceData.getZoneParentIds(zoneId)) {
      zoneIds.add(parentId)
    }
  }

  return {
    ...state,
    zoneIds,
    isFiltering: false,
  }
}

/**
 * Builds default map data with all zones set to default color.
 * Used when no data is available for the selected metric/dataset.
 *
 * @param {Object} state - The reducer state
 * @returns {Object} Map data with colors and popups
 */
const buildDefaultMapData = state => {
  const colors = {}
  for (const zone of state.raw?.resourceData?.zones || []) {
    colors[zone.id] = DEFAULT_METRIC_FILL_COLOR
  }
  return {
    colors,
    popups: {},
  }
}

export const getSemesterMapData = state => {
  const mapData = state.content.semester.mapData
  const metric = state.metrics.semester

  if (isEmptyObject(mapData) || isEmptyObject(metric)) {
    return buildDefaultMapData(state)
  }

  const out = {
    colors: {},
    popups: {},
  }

  for (const [zoneId, data] of Object.entries(mapData)) {
    const zone = state.raw?.resourceData.getZoneById(zoneId)
    out.colors[zoneId] =
      zone?.type === ZoneType.BUILDING && state.filters.zoneViewType === ZoneType.DEFAULT
        ? END_ZONE_BUILDING_FILL_COLOR
        : (data.colors[metric] ?? DEFAULT_METRIC_FILL_COLOR)
    out.popups[zoneId] = data.popupData
  }

  return out
}

export const getHourlyMapData = state => {
  const mapData = state.content.hourly.mapData

  if (isEmptyObject(mapData)) {
    return buildDefaultMapData(state)
  }

  return mapData
}

/**
 * Updates state with computed fan geojson for building view mode.
 * Only calculates when dependencies change (called from specific reducer actions).
 * Generates transformed floor and end zone features positioned below the building.
 *
 * @param {Object} state - The reducer state
 * @returns {Object} Updated state with fanGeojson
 */
export const updateFanGeojson = state => {
  const resourceData = state.raw?.resourceData
  const zone = state.resource.zone

  // Only generate fan geojson if we're in building view mode (zone has buildingId)
  if (!zone?.buildingId || isEmptyObject(resourceData)) {
    return {
      ...state,
      fanGeojson: null,
    }
  }

  const buildingFeature = resourceData?.getBuildZoneFeatureByBuildingId(zone.buildingId)
  if (!buildingFeature) {
    return {
      ...state,
      fanGeojson: null,
    }
  }

  // Get floor features for this building
  const floorFeatures =
    resourceData?.getBuildingFloorZoneFeaturesByBuildingId(zone.buildingId) ?? []

  // Filter by zoneIds if filtering is active
  let filteredFloors = floorFeatures
  if (state.zoneIds?.size > 0) {
    filteredFloors = floorFeatures.filter(feature => state.zoneIds.has(feature.properties.id))
  }

  // Build floors array with children
  const floors = filteredFloors.map(floorFeature => ({
    floor: floorFeature,
    children:
      resourceData?.getDefaultZoneFeaturesByBuildingFloor(
        zone.buildingId,
        floorFeature.properties.floor
      ) ?? [],
  }))

  // Generate fan pattern features
  const fanFeatures = createFloorFanPattern({ feature: buildingFeature, floors })

  return {
    ...state,
    fanGeojson: fanFeatures?.length ? { type: "FeatureCollection", features: fanFeatures } : null,
  }
}

export const updateData = state => {
  const resourceData = state.raw?.resourceData
  const datasourceData = state.raw?.datasourceData
  const semesterData = state.raw?.semesterData

  const resourceId = state.resource.id

  // Compute map data based on display type
  const mapData =
    state.filters.mapDisplayType === FilterIds.Options.SEMESTER
      ? getSemesterMapData(state)
      : getHourlyMapData(state)

  return {
    ...state,
    data: {
      ...state.data,
      fanGeojson: state.fanGeojson,
      resources: {
        hasFetched: resourceData != null,
        isFiltering: state.isFiltering,
        hierarchy: resourceData?.getZoneHierarchy(),
        zone: resourceData?.getZoneById(resourceId),
        zones: resourceData?.zones,
        zoneIds: state.zoneIds,
        usages: resourceData?.usages ?? EMPTY_ARRAY,
        getGeoJsonFeatures: () => {
          const zoneId = state.resource.id
          if (!zoneId) {
            return null
          }

          const data = { type: "FeatureCollection", features: [] }
          const buildingFeature = state.raw?.resourceData?.getBuildZoneFeatureByBuildingId(
            state.resource?.zone?.buildingId
          )
          if (buildingFeature) {
            data.features.push(buildingFeature)
          }

          // If in building view mode and fan geojson exists, look for the feature in the fan layout
          if (
            state.resource.zone?.buildingId &&
            state.fanGeojson &&
            state.filters.zoneViewType === ZoneType.BUILDING
          ) {
            const fanFeature = state.fanGeojson.features.find(
              f => f.properties.buildingId === state.resource.zone.buildingId
            )
            if (fanFeature) {
              for (const feature of state.fanGeojson.features) {
                data.features.push(feature)
              }
            }
          }

          return data
        },
        getCampusBounds: zoneId => {
          return resourceData?.bbox?.[zoneId]
        },
        getParent: zone => {
          return resourceData?.getZoneById(zone?.parentId)
        },
        getZoneParentIds: zoneId => {
          return resourceData?.getZoneParentIds(zoneId) ?? EMPTY_SET
        },
        subZoneIter: zone => {
          const getSubZones = zone => {
            if (!resourceData?.childZoneIdLookup?.[zone?.id]?.size) {
              return []
            }

            return Array.from(resourceData.childZoneIdLookup[zone.id]).map(zoneId =>
              resourceData.getZoneById(zoneId)
            )
          }

          function* handler(zones) {
            for (const child of zones) {
              yield child

              for (const innerChild of handler(getSubZones(child))) {
                yield innerChild
              }
            }
          }

          return Array.from(handler(getSubZones(zone)))
        },
        getFloorZones: () => {
          return resourceData?.floorZones ?? EMPTY_ARRAY
        },
        getUsage: usageId => {
          return resourceData?.getUsageById(usageId)
        },
        buildings: resourceData?.buildings ?? EMPTY_ARRAY,
        getBuilding: buildingId => {
          return resourceData?.getBuildingById(buildingId)
        },
        floors: resourceData?.floors ?? EMPTY_ARRAY,
        types: resourceData?.types ?? EMPTY_ARRAY,
        initialBounds: resourceData?.initialBounds,
      },
      semesterSummary: {
        metric: state.metrics.semester,
        table: datasourceData?.semesterTable,
        column: datasourceData?.semesterTable?.enabledColumns.find(
          c => c.name === state.metrics.semester
        ),
        zoneData: state.content.semester.zoneData?.[resourceId],
        maxValues: semesterData?.maxValues,
        colorRanges: state.colorRanges.semester?.[state.metrics.semester],
      },
      map: {
        baseGeoJson: resourceData?.geojson,
        getBuildingFeature: buildingId => {
          const data = resourceData?.getBuildZoneFeatureByBuildingId(buildingId)
          if (!data || (state.zoneIds?.size > 0 && !state.zoneIds.has(data.properties.id))) {
            return null
          }

          return data
        },
        getBuildingFloorFeatures: buildingId => {
          let data = resourceData?.getBuildingFloorZoneFeaturesByBuildingId(buildingId) ?? []
          if (state.zoneIds?.size > 0) {
            data = data.filter(feature => state.zoneIds.has(feature.properties.id))
          }

          return data
        },
        getDefaultFeatures: (buildingId, floor) => {
          let data = resourceData?.getDefaultZoneFeaturesByBuildingFloor(buildingId, floor) ?? []
          if (state.zoneIds?.size > 0) {
            data = data.filter(feature => state.zoneIds.has(feature.properties.id))
          }

          return data
        },
        mapColors: mapData.colors,
        getMapPopup: featureOrFeatures => {
          const features = Array.isArray(featureOrFeatures) ? featureOrFeatures : [featureOrFeatures]
          const feature = features[0]

          if (feature?.source === "da-campus-flow-source") {
            return generateFlowPopupContent(features)
          }

          const data = mapData.popups[feature?.properties?.id]
          if (!data) {
            return EMPTY_ZONE_DATA_TAG
          }
          return data
        },
      },
    },
  }
}

export const MapViewDataReducer = (state, action) => {
  switch (action.type) {
    case SET_DATA: {
      state = {
        ...state,
        raw: action.payload,
      }

      state = updateResourceInfo(state)
      state = updateSemsterDatasetInfo(state)
      state = updateHourlyDatasetInfo(state)
      state = updateSemesterContentInfo(state)
      state = updateHourlyContentInfo(state)
      state = updateFanGeojson(state)
      state = updateData(state)

      return state
    }

    case SET_RESOURCE_ID: {
      state = {
        ...state,
        resource: {
          id: action.payload,
        },
      }

      state = updateResourceInfo(state)
      state = updateFanGeojson(state)
      state = updateData(state)

      return state
    }

    case SET_ZONE_IDS: {
      if (state.zoneIds !== action.payload) {
        state = {
          ...state,
          zoneIds: action.payload,
        }
      }

      if (state.isFiltering) {
        state.isFiltering = false
      }

      state = updateFanGeojson(state)

      return state
    }

    case SET_FILTER_ZONE_VIEW_TYPE: {
      if (action.payload === null) {
        return state
      }

      state = {
        ...state,
        filters: {
          ...state.filters,
          zoneViewType: action.payload,
        },
      }

      state = updateHourlyContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_FILTER_MAP_DISPLAY_TYPE: {
      if (action.payload === null) {
        return state
      }

      state = {
        ...state,
        filters: {
          ...state.filters,
          mapDisplayType: action.payload,
        },
      }

      state = updateHourlyContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_FILTER_DAYS: {
      if (isEmptyArray(action.payload)) {
        return state
      }

      state = {
        ...state,
        filters: {
          ...state.filters,
          days: action.payload,
        },
      }

      state = updateHourlyContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_FILTER_HOUR: {
      if (action.payload == null) {
        return state
      }

      state = {
        ...state,
        filters: {
          ...state.filters,
          hour: action.payload,
        },
      }

      state = updateHourlyContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_SEMESTER_METRIC: {
      if (action.payload == null) {
        return state
      }

      state = {
        ...state,
        metrics: {
          ...state.metrics,
          semester: action.payload,
        },
      }

      state = updateSemsterDatasetInfo(state)
      state = updateSemesterContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_HOURLY_METRIC: {
      if (action.payload == null) {
        return state
      }

      state = {
        ...state,
        metrics: {
          ...state.metrics,
          hourly: action.payload,
        },
      }

      state = updateHourlyDatasetInfo(state)
      state = updateHourlyContentInfo(state)
      state = updateData(state)

      return state
    }

    case SET_FILTER_ZONE_SELECTION: {
      state = {
        ...state,
        filters: {
          ...state.filters,
          usages: action.payload.usages,
          buildings: action.payload.buildings,
          floors: action.payload.floors,
          loweredSearch: action.payload.loweredSearch,
        },
      }

      state = updateZoneFilters(state)
      state = updateData(state)

      return state
    }

    case SET_IS_FILTERING: {
      state = {
        ...state,
        isFiltering: action.payload,
      }

      state = updateData(state)

      return state
    }

    default:
      return state
  }
}

/**
 * Hook for consuming map view data from the context.
 * Must be used within a MapViewDataProvider component.
 *
 * @returns {MapViewDataResult} Object containing view controls, pre-built left pane data, and pre-built right pane data
 * @throws {Error} If used outside MapViewDataProvider
 */
export function useMapViewData() {
  const context = useContext(MapViewDataContext)
  if (!context) {
    throw new Error("useMapViewDataContext must be used within a MapViewDataProvider")
  }

  return context
}

// Register resource worker if not already registered
const manager = WorkerManager.getInstance()
if (!manager.isRegistered("resource")) {
  const worker = new Worker(new URL("../workers/data.js", import.meta.url))
  manager.registerWorker("resource", worker)
}

export function MapViewData() {
  const { schoolId, semester, addLoadingId, removeLoadingId } = useAppContext()

  const {
    resourceId,
    zoneType,
    mapDisplayType,
    semesterMetric,
    utilizationMetric,
    usages,
    buildings,
    floors,
    days,
    hour,
    isHidingLeftPane,
    toggleLeftPane,
    isHidingRightPane,
    toggleRightPane,
    clearFilters,
    setFilters,
  } = useFilterQueryParams()

  const { loweredSearch } = useSearchQueryParams()

  const addLoadingIdRef = useRef(addLoadingId)
  const removeLoadingIdRef = useRef(removeLoadingId)
  const setFiltersRef = useRef(setFilters)

  useEffect(() => {
    const loadingId = "useMapViewData#load"
    const manager = WorkerManager.getInstance()

    const handleResourceLoading = ({ loading }) => {
      if (loading) {
        addLoadingIdRef.current(loadingId)
      } else {
        removeLoadingIdRef.current(loadingId)
      }
    }

    const handleResourceLoaded = async ({ schoolId, semesterId }) => {
      const datasourceData = await getDatasourceData(schoolId)
      const resourceData = await getResourceData(schoolId)
      const semesterData = await getSemesterData(schoolId, semesterId)
      const hourlyData = await getHourlyData(schoolId, semesterId)
      const campusFlowData = await getCampusFlowData(schoolId, semesterId)

      // Guard: If data not yet in cache (e.g., after IndexedDB migration), wait for fetch
      if (!datasourceData || !resourceData) {
        console.debug("Map data not yet cached, waiting for fetch...")
        return
      }

      dispatch({
        type: SET_DATA,
        payload: { datasourceData, resourceData, semesterData, hourlyData, campusFlowData },
      })

      // Find "Fill Rate" column or fallback to first index
      const getDefaultMetric = columns => {
        const fillRateColumn = columns.find(c => c.label === "Fill Rate")
        return fillRateColumn ? fillRateColumn.name : columns[0].name
      }

      setFiltersRef.current({
        [FilterQueryArgs.UTILIZATION_METRIC]: getDefaultMetric(
          datasourceData.hourlyTable.enabledColumns
        ),
        [FilterQueryArgs.SEMESTER_METRIC]: getDefaultMetric(
          datasourceData.semesterTable.enabledColumns
        ),
      })

      // Mark that map data has been loaded in this session
      sessionStorage.setItem("map_data_loaded", "true")
    }

    const handleResourceStale = async ({ schoolId, semesterId, message }) => {
      console.debug("Map data stale:", message)
      // Reuse handleResourceLoaded to load stale data immediately
      await handleResourceLoaded({ schoolId, semesterId })
    }

    const handleResourceError = error => {
      console.error(error)
      ErrorToast("Unable to fetch data")
    }

    manager.addEventListener("resource", "RESOURCE_LOADING", handleResourceLoading)
    manager.addEventListener("resource", "RESOURCE_LOADED", handleResourceLoaded)
    manager.addEventListener("resource", "RESOURCE_STALE", handleResourceStale)
    manager.addEventListener("resource", "RESOURCE_ERROR", handleResourceError)

    return () => {
      manager.removeEventListener("resource", "RESOURCE_LOADING", handleResourceLoading)
      manager.removeEventListener("resource", "RESOURCE_LOADED", handleResourceLoaded)
      manager.removeEventListener("resource", "RESOURCE_STALE", handleResourceStale)
      manager.removeEventListener("resource", "RESOURCE_ERROR", handleResourceError)
    }
  }, [])

  useEffect(() => {
    if (isEmptyObject(schoolId) || isEmptyObject(semester?.id)) {
      return
    }

    const fetchData = () => {
      // Check if this is the first data load in this session (tab)
      const isFirstLoad = !sessionStorage.getItem("map_data_loaded")

      WorkerManager.getInstance().postMessage("resource", {
        type: "FETCH",
        payload: {
          schoolId,
          semesterId: semester?.id,
          isFirstLoad,
        },
      })
    }

    const handler = async () => {
      await deleteDatasourceData(schoolId)
      fetchData()
    }

    eventBus.on("athena-datasource-updated", handler)
    fetchData()

    return () => {
      eventBus.off("athena-datasource-updated", handler)
    }
  }, [schoolId, semester?.id])

  const [state, dispatch] = useReducer(MapViewDataReducer, DEFAULT_DATA)

  const resourceData = state.raw?.resourceData
  const datasourceData = state.raw?.datasourceData

  const semesterData = state.raw?.semesterData

  const hourlyData = state.raw?.hourlyData

  const campusFlowData = state.raw?.campusFlowData

  const clearFiltersRef = useRef(clearFilters)
  const toggleLeftPaneRef = useRef(toggleLeftPane)
  const toggleRightPaneRef = useRef(toggleRightPane)

  // Handle school changes - clear filters
  useEffect(() => {
    clearFiltersRef.current()
  }, [schoolId])

  useEffect(() => {
    dispatch({ type: SET_RESOURCE_ID, payload: resourceId })
  }, [resourceId])

  useEffect(() => {
    dispatch({ type: SET_FILTER_ZONE_VIEW_TYPE, payload: zoneType })
  }, [zoneType])

  useEffect(() => {
    dispatch({ type: SET_FILTER_MAP_DISPLAY_TYPE, payload: mapDisplayType })
  }, [mapDisplayType])

  useEffect(() => {
    dispatch({ type: SET_FILTER_DAYS, payload: days })
  }, [days])

  useEffect(() => {
    dispatch({ type: SET_FILTER_HOUR, payload: hour })
  }, [hour])

  useEffect(() => {
    dispatch({ type: SET_SEMESTER_METRIC, payload: semesterMetric })
  }, [semesterMetric])

  useEffect(() => {
    dispatch({ type: SET_HOURLY_METRIC, payload: utilizationMetric })
  }, [utilizationMetric])

  // Handle filter changes
  useEffect(() => {
    dispatch({
      type: SET_FILTER_ZONE_SELECTION,
      payload: { usages, buildings, floors, loweredSearch },
    })
  }, [buildings, floors, loweredSearch, resourceData, resourceId, usages])

  const view = useMemo(() => {
    return {
      hasResource: resourceId != null,
      isHidingLeftPane,
      toggleLeftPane: toggleLeftPaneRef.current,
      isHidingRightPane,
      toggleRightPane: toggleRightPaneRef.current,
    }
  }, [isHidingLeftPane, isHidingRightPane, resourceId])

  const hourlyColumn = state.columns.hourly
  const hourlyColorRanges = state.colorRanges.hourly?.[utilizationMetric]
  const apiColorRanges = state.raw?.datasourceData?.colorRanges

  const rawDayGraphData = useMemo(
    () => buildRawDayGraphData(hourlyData, state.resource?.zone, hourlyColumn, apiColorRanges),
    [apiColorRanges, hourlyColumn, hourlyData, state.resource?.zone]
  )

  const rawHourGraphData = useMemo(
    () => buildRawHourGraphData(hourlyData, state.resource?.zone, hourlyColumn, apiColorRanges),
    [apiColorRanges, hourlyColumn, hourlyData, state.resource?.zone]
  )

  // todo: move this into the reducer
  return {
    view,
    resources: state.data.resources,
    datasourceMappings: {
      isLoaded: datasourceData != null,
      datasets: datasourceData?.datasets ?? EMPTY_ARRAY,
      hasDatasets: datasourceData?.datasets?.length > 0,
      groups: datasourceData?.groups,
      actions: {
        editMapping: (datasourceId, newData) => {
          const loadingId = "useDatasourceMappings#editMapping"
          addLoadingIdRef.current(loadingId)
          const controller = new AbortController()
          Api.put(`settings/datasource/${datasourceId}`, newData, { signal: controller.signal })
            .then(parseDatasourceResponse)
            .then(data => {
              dispatch({
                type: SET_DATA,
                payload: {
                  datasourceData: data,
                  resourceData,
                  semesterData,
                  hourlyData,
                  campusFlowData,
                },
              })

              return data
            })
            .then(data => setDatasourceData(schoolId, data))
            .catch(e => {
              if (e.handled) return

              console.error(e.error)
              ErrorToast("Unable to update Datasource")
            })
            .finally(() => removeLoadingIdRef.current(loadingId))
        },
        editColumn: (datasourceId, columnId, newData) => {
          if (!schoolId) {
            return
          }

          const loadingId = "useDatasourceMappings#editColumn"
          addLoadingIdRef.current(loadingId)
          const controller = new AbortController()
          Api.put(
            `schools/${schoolId}/map/datasource/${datasourceId}/column/${columnId}`,
            newData,
            {
              signal: controller.signal,
            }
          )
            .then(parseDatasourceResponse)
            .then(data => {
              dispatch({
                type: SET_DATA,
                payload: {
                  datasourceData: data,
                  resourceData,
                  semesterData,
                  hourlyData,
                  campusFlowData,
                },
              })

              return data
            })
            .then(data => setDatasourceData(schoolId, data))
            .catch(e => {
              if (e.handled) return

              console.error(e.error)
              ErrorToast("Unable to update Datasource Column")
            })
            .finally(() => removeLoadingIdRef.current(loadingId))
        },
      },
      semesterTable: datasourceData?.semesterTable,
      hourlyTable: datasourceData?.hourlyTable,
    },
    semesterSummary: state.data.semesterSummary,
    hourlySummary: {
      metric: utilizationMetric,
      table: datasourceData?.hourlyTable,
      column: hourlyColumn,
      defaultHourlyGraphMetric: EMPTY_HOURLY_PANE_DATA,
      rawDayGraphData,
      rawHourGraphData,
      legend: buildHourlyLegend(hourlyColumn, hourlyColorRanges),
      colorRanges: hourlyColorRanges,
      maxValues: hourlyData?.maxValues,
    },
    map: state.data.map,
    fanGeojson: state.data.fanGeojson,
    campusFlowData,
  }
}

/**
 * Builds day-based graph data with labels, colors, and value getters for visualization.
 * Creates a graph showing metric values across days (y-axis) and hours (x-axis).
 * Automatically determines scale based on max value, with minimum of 100% for percentage metrics.
 *
 * @param {Object} rawData - Raw data object containing byDayData with all metric values
 * @param {Object} resource - Resource/zone object with id property
 * @param {Object} column - DatasourceColumn object with metric metadata (name, formatting, type)
 * @returns {Object|null} Graph configuration object or null if data is invalid
 * @returns {Object} returns.labels - X and Y axis labels
 * @returns {Array<string>} returns.labels.y - Day names (Mon, Tue, etc.)
 * @returns {Array<string|null>} returns.labels.x - Hour labels (4am, 8am, etc.)
 * @returns {Function} returns.get - Function(day, hour) that returns metric body for a cell
 * @returns {Object} returns.legend - Legend configuration with color ranges
 *
 * @example
 * const rawData = { byDayData: { "zone-123": { "fill_rate": {...} } } }
 * const resource = { id: "zone-123" }
 * const column = { name: "fill_rate", isPercentageMetric: () => true, formatValue: (v) => `${v}%` }
 * const apiColorRanges = { percentage: [...], default: [...] }
 * buildRawDayGraphData(rawData, resource, column, apiColorRanges)
 * // Returns { labels: {...}, get: fn, legend: {...} }
 */
export const buildRawDayGraphData = (rawData, resource, column, apiColorRanges) => {
  if (isEmptyObject(rawData) || isEmptyObject(resource) || isEmptyObject(column)) {
    return null
  }

  const values = getMaxDayGraphValue(rawData?.byDayData?.[resource.id]?.[column.name])
  if (isEmptyObject(values)) {
    return null
  }

  const { data: dayData, max: baseMaxValue } = values
  let maxValue = baseMaxValue
  if (column.isPercentageMetric() && maxValue < 1) {
    maxValue = 1
  }

  const colorRange = buildColorRanges(column, maxValue, apiColorRanges)

  const yLabelValues = Days.map(d => d.shortName)

  return buildGraphOutput(
    yLabelValues,
    colorRange,
    null,
    {
      start: column.isPercentageMetric() ? "0%" : "0",
      end: column.formatValue(maxValue),
    },
    (day, hour) => {
      const value = dayData?.[day]?.[hour]
      const color = getColor(colorRange, value)
      return buildHourlyGraphMetricBody(column, value, colorRange, maxValue, {
        label: formatString(column.formatValue(value)),
        color,
      })
    }
  )
}

/**
 * Builds hour-based graph data with dynamically calculated y-axis labels and capacity indicators.
 * Creates a graph showing averaged metric values across hours (x-axis).
 * For percentage metrics, scales to 100%+ with rounding. For integer metrics, includes resource
 * capacity comparison when available (for DEFAULT zone types only).
 *
 * @param {Object} rawData - Raw data object containing byHourData with all metric values
 * @param {Object} resource - Resource/zone object with id, type, and optional capacity properties
 * @param {Object} column - DatasourceColumn object with metric metadata (name, formatting, type, symbol)
 * @returns {Object|null} Graph configuration object or null if data is invalid
 * @returns {Object} returns.labels - X and Y axis labels
 * @returns {Array<string>} returns.labels.y - Formatted value labels (e.g., "120%", "90%", etc.)
 * @returns {Array<string|null>} returns.labels.x - Hour labels (4am, 8am, etc.)
 * @returns {Function} returns.get - Function(hour) that returns metric body for an hour
 * @returns {string|null} returns.capacity - Formatted capacity percentage if applicable
 * @returns {Object} returns.legend - Legend configuration with color ranges and anchors
 *
 * @example
 * const rawData = { byHourData: { "zone-123": { "fill_rate": {...} } } }
 * const resource = { id: "zone-123", type: "default", capacity: 50 }
 * const column = { name: "fill_rate", isPercentageMetric: () => false, symbol: "", formatValue: (v) => v }
 * const apiColorRanges = { percentage: [...], default: [...] }
 * buildRawHourGraphData(rawData, resource, column, apiColorRanges)
 * // Returns { labels: {...}, get: fn, capacity: "42%", legend: {...} }
 */
export const buildRawHourGraphData = (rawData, resource, column, apiColorRanges) => {
  if (isEmptyObject(rawData) || isEmptyObject(resource) || isEmptyObject(column)) {
    return null
  }

  const values = getMaxHourGraphValue(rawData?.byHourData?.[resource?.id]?.[column.name])
  if (isEmptyObject(values)) {
    return null
  }

  const { data: dayData, max: baseMaxValue } = values
  let maxValue = baseMaxValue
  if (column.isPercentageMetric() && maxValue < 1) {
    maxValue = 1
  }

  const colorRange = buildColorRanges(column, maxValue, apiColorRanges)

  const yLabelValues = []
  let capacity = null
  let maxYValue

  if (column.isPercentageMetric()) {
    maxYValue = parseInt(maxValue * 100)
    if (maxYValue > 100) {
      maxYValue += Y_GRAPH_PERCENTAGE_LABEL_STEP - (maxYValue % Y_GRAPH_PERCENTAGE_LABEL_STEP)
    }
  } else {
    maxYValue = parseInt(maxValue)

    if (resource.type === ZoneType.DEFAULT) {
      if (resource.capacity) {
        if (maxYValue < resource.capacity) {
          maxYValue = resource.capacity
          capacity = 1
        } else {
          maxYValue += maxYValue * Y_GRAPH_INTEGER_LABEL_STEP
          capacity = resource.capacity / maxYValue
        }
      }
    } else {
      maxYValue += maxYValue * Y_GRAPH_INTEGER_LABEL_STEP
    }
  }

  const step = maxYValue / (Y_GRAPH_LABEL_COUNT - 1)
  for (let i = 0; i < Y_GRAPH_LABEL_COUNT; i++) {
    const y = maxYValue - i * step
    let value = formatNumber(Math.round(y))
    if (column.symbol) {
      value += column.symbol
    }

    yLabelValues.push(value)
  }

  if (column.isPercentageMetric()) {
    // convert back to float
    maxYValue /= 100
  }

  if (capacity != null) {
    capacity = formatPercentage(capacity, { isNormalized: true })
  }

  return buildGraphOutput(
    yLabelValues,
    colorRange,
    capacity,
    {
      start: yLabelValues[yLabelValues.length - 1],
      end: yLabelValues[0],
    },
    hour => {
      const value = dayData?.[hour]
      const color = getColor(colorRange, value)
      return buildHourlyGraphMetricBody(column, value, colorRange, maxYValue, {
        label: formatString(column.formatValue(value)),
        color,
      })
    }
  )
}

/**
 * Creates a standardized graph output object with labels, legend, and data getter function.
 * Used internally by buildRawDayGraphData and buildRawHourGraphData to provide consistent structure.
 *
 * @param {Array<string>} yLabelValues - Y-axis labels (days or numeric values)
 * @param {Array<Object>} colorRange - Array of color range objects with title, range, and color properties
 * @param {string|null} capacity - Formatted capacity percentage string or null
 * @param {Object} anchor - Legend anchor points with start and end values
 * @param {Function} getter - Function to retrieve metric data for specific coordinates
 * @returns {Object} Standardized graph configuration object
 */
const buildGraphOutput = (yLabelValues, colorRange, capacity, anchor, getter) => ({
  labels: {
    y: yLabelValues,
    x: getHourLabels(),
  },
  get: getter,
  capacity,
  legend: {
    show: true,
    count: colorRange.length,
    anchor,
    get: index => {
      const range = colorRange[index]
      return {
        title: range.title,
        label: range.range,
        color: range.color,
      }
    },
  },
})

/**
 * Finds the maximum value across all days and hours for day graph visualization.
 * Iterates through all days (1-7) and hours (0-23) to find the highest metric value.
 * Used to determine the appropriate scale for day-based graphs.
 *
 * @param {Object} values - Object with days (1-7) as keys, each containing hours (0-23) as keys with metric values
 * @returns {Object|null} Object with {data: Object, max: number} or null if values are invalid
 * @returns {Object} returns.data - Original values object passed through unchanged
 * @returns {number|null} returns.max - Maximum value found across all days/hours, or null if no values
 *
 * @example
 * const values = {
 *   "1": { "0": 0.195, "1": 0.269, ..., "23": 0.043 },
 *   "2": { "0": 0.154, "1": 0.175, ..., "23": 0.042 },
 *   ...
 *   "7": { "0": 0.043, "1": 0.042, ..., "23": 0.347 }
 * }
 * getMaxDayGraphValue(values)
 * // Returns { data: values, max: 1.611 }
 */
export const getMaxDayGraphValue = values => {
  if (isEmptyObject(values)) {
    return null
  }

  let maxValue = null

  for (let day = 1; day < 8; day++) {
    for (let hour = 0; hour < 24; hour++) {
      const value = values[day]?.[hour]
      if (value == null) {
        continue
      }

      if (maxValue == null) {
        maxValue = 0
      }
      maxValue = Math.max(maxValue, value)
    }
  }

  return { data: values, max: maxValue }
}

/**
 * Calculates the maximum mean value across all hours for hourly graph visualization.
 * For each hour (0-23), computes the mean of all day values (1-7), then returns both
 * the array of hourly means and the maximum value. Used to determine graph scale.
 *
 * @param {Object} values - Object with hours (0-23) as keys, each containing days (1-7) as keys with metric values
 * @returns {Object|null} Object with {data: Array, max: number} or null if values are invalid
 * @returns {Array} returns.data - Array of 24 mean values, one for each hour
 * @returns {number} returns.max - Maximum value from the hourly means
 *
 * @example
 * const values = {
 *   "0": { "1": 0.195, "2": 0.269, ..., "7": 0.043 },
 *   "1": { "1": 0.106, "2": 0.154, ..., "7": 0.042 },
 *   ...
 *   "23": { "1": 0.378, "2": 0.428, ..., "7": 0.342 }
 * }
 * getMaxHourGraphValue(values)
 * // Returns { data: [0.19, 0.11, ..., 0.35], max: 1.485 }
 */
export const getMaxHourGraphValue = values => {
  if (isEmptyObject(values)) {
    return null
  }

  const data = []
  for (let hour = 0; hour < 24; hour++) {
    const hourValues = []
    for (let day = 1; day < 8; day++) {
      hourValues.push(values[hour][day])
    }

    data.push(mean(hourValues))
  }

  return { data, max: Math.max(...data) }
}

/**
 * Generates x-axis hour labels for graph visualization.
 * Shows formatted time labels at 4-hour intervals (4am, 8am, 12pm, etc.), with nulls for other hours.
 *
 * @returns {Array<string|null>} Array of 24 elements with time labels at 4-hour intervals
 *
 * @example
 * getHourLabels()
 * // Returns [null, null, null, null, "4am", null, null, null, "8am", ..., "8pm", null, null, null]
 */
export const getHourLabels = () => {
  const xLabelValues = []
  for (let hour = 0; hour < 24; hour++) {
    xLabelValues.push(hour % 4 === 0 && hour > 0 ? convertHourToTime(hour) : null)
  }

  return xLabelValues
}
